function convertToCents(value) {
  if (value == null) return null;
  // Convert value like "$27.55" or "29.56" into cents (e.g., "2755", "2956")
  // Remove any currency symbols and commas, then parse and convert to integer cents
  const num = parseFloat(String(value).replace(/[^0-9.]/g, ""));
  return isNaN(num) ? null : Math.round(num * 100);
}

class BuyBoxStickyCta extends Theme.Component {
  connectedCallback() {
    this.defineElements({
      price: "[data-bb-price]",
      planSummary: "[data-bb-plan-summary]",
      comparePrice: "[data-bb-compare-price]",
      submitBtn: "[data-bb-submit]"
    });

    this.form = this.closest("form");
    this.globalVariantId = this.data.initial_variant_id || this.data.variants?.[0]?.id || null;
    this.globalSellingPlanId = this.data.initial_selling_plan || null;
    this.syncFromForm();

    // Plan cards (assets/buy-box-plans.js) know the real plan price: quantity,
    // first-order discount and gift tiers. Prefer it over allocation math.
    const formId = this.form?.getAttribute("id");
    this.planDetail = formId ? window.EvidentBuyBoxPlans?.current?.[formId] || null : null;

    // Hide only while the buy box's own submit button is on screen: the bar
    // triggers that same submit, so showing both would duplicate the CTA.
    this.targetVisibility = new Map();
    const scope = this.form || document;
    let targets = [
      ...scope.querySelectorAll("product-form-submit:not(.buy-box-one-time-submit)")
    ];
    if (targets.length === 0) {
      targets = [...document.querySelectorAll(".product-buy-box-info")];
    }

    this.setVisible(false);
    if (targets.length > 0) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.targetVisibility.set(entry.target, entry.isIntersecting);
          });
          const anyVisible = Array.from(this.targetVisibility.values()).some((v) => v);
          this.setVisible(!anyVisible);
        },
        { threshold: 0 }
      );
      targets.forEach((t) => this.observer.observe(t));
    }

    document.addEventListener(
      "productformvariants:change",
      (event) => {
        const id = event.detail?.variant?.id;
        if (id) {
          this.globalVariantId = id;
          requestAnimationFrame(() => {
            this.syncFromForm();
            this.render();
          });
        }
      },
      { signal: this.controller.signal }
    );

    document.addEventListener(
      window.EvidentBuyBoxPlans?.EVENT_NAME || "evident:selling_plan:change",
      (event) => {
        const detail = event.detail;
        if (!detail || (formId && detail.formId && detail.formId !== formId)) return;
        this.planDetail = detail;
        this.syncFromForm();
        this.render();
      },
      { signal: this.controller.signal }
    );

    document.addEventListener(
      "change",
      (event) => {
        const target = event.target;
        if (!this.form || !target || !this.form.contains(target)) return;

        if (target.name === "selling_plan" || target.matches("[data-bbp-subscribe-select]")) {
          this.syncFromForm();
          this.render();
        }
      },
      { signal: this.controller.signal }
    );

    this.elements.submitBtn?.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        this.handleSubmit();
      },
      { signal: this.controller.signal }
    );

    this.render();
  }

  get controller() {
    return this._controller || (this._controller = new AbortController());
  }

  get staticPrice() {
    if (this.dataset.staticPrice) {
      return convertToCents(this.dataset.staticPrice);
    }

    return null;
  }

  get staticComparePrice() {
    if (this.dataset.staticComparePrice) {
      return convertToCents(this.dataset.staticComparePrice);
    }

    return null;
  }

  get shouldChangeCtaText() {
    // Liquid writes "true"/"false"; Boolean("false") would be true.
    return this.dataset.showPricingInButton !== "true";
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this._resizeObserver?.disconnect();
    this.setScrollPadding(false);
    this._primaryLoadingObserver?.disconnect();
    this._controller?.abort();
    super.disconnectedCallback?.();
  }

  currentVariant() {
    if (!this.data.variants) return null;
    return (
      this.data.variants.find((v) => v.id === this.globalVariantId) || this.data.variants[0]
    );
  }

  syncFromForm() {
    if (!this.form) return;

    const variantInput = this.form.querySelector("[name='id']");
    if (variantInput?.value) {
      this.globalVariantId = Number(variantInput.value);
    }

    const checkedPlan = this.form.querySelector("input[name='selling_plan']:checked");
    const hiddenPlan = this.form.querySelector("input[name='selling_plan'][type='hidden']");
    const planInput = checkedPlan || hiddenPlan;
    const value = planInput?.value;
    this.globalSellingPlanId = value ? Number(value) : null;
  }

  currentAllocation() {
    const variant = this.currentVariant();
    if (!variant || !this.globalSellingPlanId) return null;
    return variant.selling_plan_allocations?.find(
      (a) => a.selling_plan_id === this.globalSellingPlanId
    );
  }

  // The plan detail only counts while its plan is still the selected one
  // (a stale detail can outlive a switch to a non-card selling plan input).
  currentPlanDetail() {
    const plan = this.planDetail;
    if (!plan || plan.price == null) return null;
    if (Number(plan.id) !== this.globalSellingPlanId) return null;
    // Cards are per variant; two variants may share a selling plan.
    if (plan.variantId && plan.variantId !== this.globalVariantId) return null;
    return plan;
  }

  // "Plan" layout: "90-Day Supply · 3 gifts". No gifts → title only.
  renderPlanSummary(plan) {
    const el = this.elements.planSummary;
    if (!el || !plan.title) return;

    const count = Number(plan.giftCount) || 0;
    if (count === 0) {
      el.textContent = plan.title;
      return;
    }

    const gifts = `${count} ${count === 1 ? "gift" : "gifts"}`;
    el.textContent = (el.dataset.format || "[title] · [gifts]")
      .replace(/\[title\]/g, plan.title)
      .replace(/\[gifts\]/g, gifts);
  }

  render() {
    const variant = this.currentVariant();
    if (!variant) return;

    const allocation = this.currentAllocation();
    let price = allocation ? allocation.price : variant.price;
    let compare = allocation
      ? (allocation.compare_at_price || 0) + (this.data.subscription_gift_compare_total || 0)
      : variant.compare_at_price;

    const plan = this.currentPlanDetail();
    if (plan) {
      price = plan.price;
      compare = plan.compareAtPrice;
      this.renderPlanSummary(plan);
    } else {
      // Manual overrides only apply without plan cards.
      if (this.staticPrice) {
        price = this.staticPrice;
      }

      if (this.staticComparePrice) {
        compare = this.staticComparePrice;
      }
    }

    if (this.elements.price) {
      this.elements.price.textContent = this.formatMoney(price);
    }

    if (this.elements.comparePrice) {
      // Write the amount only: the <s> also holds a hidden "Regular price" label.
      const compareAmount =
        this.elements.comparePrice.querySelector("[data-bb-compare-amount]") ||
        this.elements.comparePrice;
      if (compare && compare > price) {
        compareAmount.textContent = this.formatMoney(compare);
        this.elements.comparePrice.style.display = "";
      } else {
        compareAmount.textContent = "";
        this.elements.comparePrice.style.display = "none";
      }
    }

    if (this.elements.submitBtn) {
      const available = variant.available !== false;
      this.elements.submitBtn.disabled = !available;

      if (this.shouldChangeCtaText) {
        const ctaText = this.elements.submitBtn.dataset.bbCtaText || "Add to cart";
        const spinner = this.elements.submitBtn.querySelector(".spinner");
        this.elements.submitBtn.textContent = available ? ctaText : "Out of stock";
        if (spinner) this.elements.submitBtn.appendChild(spinner);
      }
    }
  }

  // Off-screen bar stays out of the tab order and the accessibility tree.
  setVisible(visible) {
    this.classList.toggle("is-visible", visible);
    this.inert = !visible;
    this.setScrollPadding(visible);
  }

  // While the bottom bar is up, reserve its height so focused elements and
  // anchor jumps aren't scrolled underneath it (WCAG 2.4.11).
  setScrollPadding(visible) {
    if (this.classList.contains("position-top")) return;
    const root = document.documentElement;

    if (!visible) {
      this._resizeObserver?.disconnect();
      root.style.removeProperty("scroll-padding-bottom");
      return;
    }

    const update = () => {
      root.style.scrollPaddingBottom = `${this.offsetHeight}px`;
    };
    update();
    this._resizeObserver ??= new ResizeObserver(update);
    this._resizeObserver.observe(this);
  }

  setLoading(isLoading) {
    this.elements.submitBtn?.classList.toggle("state--loading", isLoading);
  }

  // The bar shows whenever the buy box's own submit is off screen. Over the
  // buy box (e.g. scrolled to the plan cards) it submits; elsewhere on the
  // page it scrolls to the form instead, like a link CTA.
  get buyBoxInfo() {
    return (
      this.closest(".product-buy-box-info") ||
      this.form?.closest(".product-buy-box-info") ||
      document.querySelector(".product-buy-box-info")
    );
  }

  isBuyBoxInView() {
    const info = this.buyBoxInfo;
    if (!info) return true;
    const rect = info.getBoundingClientRect();
    // The bar itself covers part of the viewport; don't count that strip.
    const bar = this.getBoundingClientRect();
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    const top = this.classList.contains("position-top") ? bar.bottom : 0;
    const bottom = this.classList.contains("position-top") ? viewport : viewport - bar.height;
    return rect.bottom > top && rect.top < bottom;
  }

  scrollToForm() {
    const target = this.form || this.buyBoxInfo;
    if (!target) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // A top bar would cover the form's first lines; leave room for it.
    const offset = (this.classList.contains("position-top") ? this.offsetHeight : 0) + 16;
    window.scrollTo({
      top: Math.max(target.getBoundingClientRect().top + window.scrollY - offset, 0),
      behavior: reduceMotion ? "auto" : "smooth"
    });

    // Keyboard / screen reader users land on the plan they're buying.
    const focusTarget =
      target.querySelector("input[name='selling_plan']:checked") ||
      target.querySelector("product-form-submit:not(.buy-box-one-time-submit) button");
    focusTarget?.focus({ preventScroll: true });
  }

  async handleSubmit() {
    const btn = this.elements.submitBtn;
    if (!btn || btn.classList.contains("state--loading") || btn.disabled) return;

    if (!this.isBuyBoxInView()) {
      this.scrollToForm();
      return;
    }

    const primarySubmit = this.form?.querySelector(
      "product-form-submit:not(.buy-box-one-time-submit) button"
    );

    if (primarySubmit) {
      this.syncFromForm();
      this.setLoading(true);

      const clearLoading = () => {
        this.setLoading(false);
        this._primaryLoadingObserver?.disconnect();
        this._primaryLoadingObserver = null;
      };

      // Clear when the primary submit finishes (success or error).
      this._primaryLoadingObserver?.disconnect();
      this._primaryLoadingObserver = new MutationObserver(() => {
        if (!primarySubmit.classList.contains("state--loading")) {
          clearLoading();
        }
      });
      this._primaryLoadingObserver.observe(primarySubmit, {
        attributes: true,
        attributeFilter: ["class"]
      });

      primarySubmit.click();

      // Primary's click handler sets loading synchronously; if it didn't, abort.
      if (!primarySubmit.classList.contains("state--loading")) {
        clearLoading();
      }
      return;
    }

    this.setLoading(true);

    const body = {
      items: [{ id: this.globalVariantId, quantity: 1 }]
    };
    if (this.globalSellingPlanId) {
      body.items[0].selling_plan = this.globalSellingPlanId;
    }

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        throw new Error(`Add to cart failed: ${response.status}`);
      }
      const data = await response.json();
      // Only attach the auto-apply code for subscription adds (see product-form-submit).
      const cart = this.globalSellingPlanId
        ? await this.applyDiscount().catch((error) => {
            console.error("[BuyBoxStickyCta] discount update failed", error);
          })
        : null;
      document.dispatchEvent(new CustomEvent("addtocart", { detail: cart || data }));
    } catch (error) {
      console.error("[BuyBoxStickyCta] add to cart failed", error);
    } finally {
      this.setLoading(false);
    }
  }

  applyDiscount() {
    const discountCode = this.form?.dataset.autoDiscountCode;
    if (!discountCode) return Promise.resolve(null);
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";

    return fetch(`${root}cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        discount: discountCode
      })
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Discount update failed: ${response.status}`);
        }
        return response.json();
      });
  }

  formatMoney(cents) {
    if (cents == null) return "";
    const dollars = cents / 100;
    return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  }
}

customElements.define("component-buy-box-sticky-cta", BuyBoxStickyCta);
